import intersection from 'lodash/intersection';
import union from 'lodash/union';
import { applySnapshot, cast, flow, Instance, types } from 'mobx-state-tree';
import { IObject } from 'types';
import { Areas, Fias } from 'http-requests';
import { ISelectValue } from 'components/ui';

import { RequestStateModel } from './general-models';

const requestStatuses = types.model({
  fiasTree: types.optional(RequestStateModel, {}),
  fiasTreeWithSectors: types.optional(RequestStateModel, {}),
  houseGroups: types.optional(RequestStateModel, {}),
  houseGroupsWithSectors: types.optional(RequestStateModel, {}),
  areasByHouse: types.optional(RequestStateModel, {}),
  flatCount: types.optional(RequestStateModel, {}),
  searchTree: types.optional(RequestStateModel, {}),
  searchTreeWithSectors: types.optional(RequestStateModel, {}),
});

const HouseGroupsModel = types.model({
  id: types.identifier,
  provider: types.string,
  title: types.string,
  houses: types.array(types.string),
  hidden: types.boolean,
  is_total: types.boolean,
  is_deleted: types.maybeNull(types.boolean),
});

export interface IHouseGroups extends Instance<typeof HouseGroupsModel> {}

export const FiasStore = types
  .model({
    tree: types.optional(types.frozen<{ [propName: string]: any }[]>(), []),
    allAvailableSectors: types.optional(types.array(types.string), []),
    selectedSectors: types.optional(types.array(types.string), []),
    treeWithSectors: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ),
    houseGroups: types.optional(types.array(HouseGroupsModel), []),
    houseGroupsWithSectors: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ),
    areas: types.optional(types.frozen<{ [propName: string]: any }[]>(), []), // список квартир для select
    statuses: types.optional(requestStatuses, {}),
    currentFiasSearchType: types.optional(types.string, 'houses'), //Выбранный тип поиска: дома/группы
    currentValueSearch: types.optional(types.string, ''), //Текущее значение в инпуте для поиска улиц/групп
    currentValueFlatSearch: types.optional(types.string, ''), //Текущее значение в инпуте для поиска квартир
    showSecondOptionsField: types.optional(types.boolean, false), //Показать второй список в поиске по адресам
    flatLivingCount: types.optional(types.number, 0),
    flatNotLivingCount: types.optional(types.number, 0),
    flatParkingCount: types.optional(types.number, 0),
    searchTree: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ),
    searchTreeWithSectors: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ),
    currentSelectedHouses: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Не подтвержденные выбранные адреса/группы
    currentSelectedFlats: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Не подтвержденные выбранные квартиры
    lastSelectedHouse: types.optional(
      types.frozen<{ [propName: string]: any }>(),
      []
    ), //Последний выбранный адрес
    confirmedSelectedHouses: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Подтвержденные выбранные адреса/группы
    confirmedSelectedFlats: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Подтвержденные выбранные квартиры
    selectedGroups: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Выходной массив по искомым группам
    selectedFias: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Выходной массив по искомым адресам
    selectedHouses: types.optional(
      types.frozen<{ [propName: string]: any }[]>(),
      []
    ), //Выходной массив по искомым домам
  })
  .volatile(() => ({
    areasResultsGeneration: 0,
    areasLoadingGeneration: 0,
  }))
  .views((self) => ({
    //TODO: сейчас используется только в неиспользуемой функции, но скорей всего пригодится в ближайшем будущем
    getFiasParent(parentId: string): any {
      //находить родителя (и в целом любой фиас)
      const initialTree = self.treeWithSectors.length
        ? self.treeWithSectors
        : self.tree;

      let result = {};

      const searchParent = (tree: any[], parentId: string): any => {
        tree.some((item: any) => {
          if (item.AOGUID === parentId) {
            result = item;

            return item;
          } else if (item.inheritors) {
            searchParent(item.inheritors, parentId);
          }

          return {};
        });
      };

      searchParent(initialTree, parentId);

      return result;
    },
    get treeWithSectorsFiltered() {
      if (!self.selectedSectors.length) {
        return self.treeWithSectors;
      }

      const treeSearcher = (initTree: any[]): any[] => {
        const tree = initTree || [];

        return tree
          .map((node) => {
            if (node.inheritors) {
              return {
                ...node,
                inheritors: treeSearcher(node.inheritors),
              };
            } else if (node.houses) {
              return {
                ...node,
                houses: treeSearcher(node.houses),
              };
            }

            return node;
          })
          .filter((node) => {
            return (
              !!intersection(self.selectedSectors, node.sectors).length &&
              self.selectedSectors.length <=
                intersection(self.selectedSectors, node.sectors).length
            );
          });
      };

      return treeSearcher(self.treeWithSectors);
    },
    get houseGroupsWithSectorsFiltered() {
      if (!self.selectedSectors.length) {
        return self.houseGroupsWithSectors;
      }

      return self.houseGroupsWithSectors.filter(
        (node) =>
          !!intersection(self.selectedSectors, node.sectors).length &&
          self.selectedSectors.length <=
            intersection(self.selectedSectors, node.sectors).length
      );
    },
    get searchTreeWithSectorsFiltered() {
      //TODO: почти то же самое что treeWithSectorsFiltered, надо упростить и заменить на один геттер либо вынести функционал в общую функцию
      if (!self.selectedSectors.length) {
        return self.searchTreeWithSectors;
      }

      const treeSearcher = (initTree: any[]): any[] => {
        const tree = initTree || [];

        return tree
          .map((node) => {
            if (node.inheritors) {
              return {
                ...node,
                inheritors: treeSearcher(node.inheritors),
              };
            } else if (node.houses) {
              return {
                ...node,
                houses: treeSearcher(node.houses),
              };
            }

            return node;
          })
          .filter((node) => {
            //TODO: кажется не хватает поиска по currentValueSearch (не проверено)
            return (
              !!intersection(self.selectedSectors, node.sectors).length &&
              self.selectedSectors.length <=
                intersection(self.selectedSectors, node.sectors).length
              // &&
              // (node.name?.includes(self.currentValueSearch) ||
              //   node.short_address?.includes(self.currentValueSearch))
            );
          });
      };

      return treeSearcher(self.searchTreeWithSectors);
    },
    get housesList(): ISelectValue[] {
      function getAddresses(
        fiasItem: IObject,
        label: string = '',
        result: { label: string; value: string }[] = []
      ): { label: string; value: string }[] {
        if (fiasItem.houses) {
          fiasItem.houses.forEach((house: IObject): void => {
            result.push({
              label: `${label}${fiasItem.name},д. ${house.short_address}`
                .replace('г ', 'г. ')
                .replace('п ', 'п. ')
                .replace('ул ', 'ул. ')
                .replace('обл ', 'обл. ')
                .replace(', ', ',')
                .split(',')
                .join(', '),
              value: house._id,
            });
          });
        }

        if (fiasItem.inheritors) {
          fiasItem.inheritors.forEach((inheritor: IObject): void => {
            getAddresses(inheritor, `${label}${fiasItem.name},`, result);
          });
        }

        return result;
      }

      return union(...self.tree.map((item: IObject) => getAddresses(item)));
    },
    get housesListWithSectors(): ISelectValue[] {
      function getAddresses(
        fiasItem: IObject,
        label: string = '',
        result: { label: string; value: string }[] = []
      ): { label: string; value: string }[] {
        if (fiasItem.houses) {
          fiasItem.houses.forEach((house: IObject): void => {
            result.push({
              label: `${label}${fiasItem.name},д. ${house.short_address}`
                .replace('г ', 'г. ')
                .replace('п ', 'п. ')
                .replace('ул ', 'ул. ')
                .replace('обл ', 'обл. ')
                .replace(', ', ',')
                .split(',')
                .join(', '),
              value: house._id,
            });
          });
        }

        if (fiasItem.inheritors) {
          fiasItem.inheritors.forEach((inheritor: IObject): void => {
            getAddresses(inheritor, `${label}${fiasItem.name},`, result);
          });
        }

        return result;
      }

      return union(
        ...self.treeWithSectors.map((item: IObject) => getAddresses(item))
      );
    },
    get groupList(): ISelectValue[] {
      return self.houseGroups.map((group: IObject) => {
        return {
          label: group.title,
          value: group.id,
        };
      });
    },
    get groupListWithSectors(): ISelectValue[] {
      return self.houseGroupsWithSectors.map((group: IObject) => {
        return {
          label: group.title,
          value: group.id,
        };
      });
    },
    //TODO: на всякий случай оставлена старая версия

    // get addressesList() {
    //   //TODO: это какая-то шляпа, надо оптимизировать на любую глубину дерева нормальной рекурсией
    //   let defaultTree = self.tree;
    //   let result: any[] = [];
    //
    //   defaultTree.forEach((item: any) => {
    //     if (item.houses) {
    //       item.houses.forEach((house: any) => {
    //         result.push({
    //           label: `${item.name}, д.${house.short_address}`,
    //           value: house._id,
    //         });
    //       });
    //     } else {
    //       item.inheritors.forEach((inherit: any) => {
    //         if (inherit.houses) {
    //           inherit.houses.forEach((house: any) => {
    //             result.push({
    //               label: `${item.name}, ${inherit.name}, д.${house.short_address}`,
    //               value: house._id,
    //             });
    //           });
    //         } else {
    //           //TODO: дальше оно может повторяться сколь угодно раз
    //           if (inherit.inheritors) {
    //             inherit.inheritors.forEach((inherit2: any) => {
    //               if (inherit2.houses) {
    //                 inherit2.houses.forEach((house2: any) => {
    //                   result.push({
    //                     label: `${item.name}, ${inherit.name}, ${inherit2.name}, д.${house2.short_address}`,
    //                     value: house2._id,
    //                   });
    //                 });
    //               }
    //             });
    //           }
    //         }
    //       });
    //     }
    //   });
    //   self.houseGroups.forEach((group) => {
    //     result.unshift({
    //       label: group.title,
    //       value: group.id,
    //     });
    //   });
    //   return result;
    // },
    get currentHouseFlatList(): ISelectValue[] {
      return (
        self.areas
          .map((area: any) => {
            return {
              label: area.str_number_full,
              value: area.id,
            };
          })
          .filter((area: any): boolean => !!area.label) || []
      );
    },
    getAllHousesFromTree(
      fiasTreeLevel?: { [propName: string]: any },
      onlyIds?: boolean
    ) {
      // Необходимо учитывать случаи, когда на определенных уровнях могут
      // быть и houses и inheritors (могут попадаться дома без улиц)
      return (fiasTreeLevel || self.tree)
        .map((fiasLevel: { [propName: string]: any }) =>
          fiasLevel.houses
            ? [
                ...fiasLevel.houses.map((houseObj: any) =>
                  onlyIds ? houseObj._id : houseObj
                ),
                fiasLevel.inheritors
                  ? this.getAllHousesFromTree(fiasLevel.inheritors, onlyIds)
                  : [],
              ]
            : this.getAllHousesFromTree(fiasLevel.inheritors, onlyIds)
        )
        .flat(Infinity);
    },
    getHouseById(houseId: string, fiasTreeLevel?: IObject): IObject {
      let house: IObject = {};
      (fiasTreeLevel || self.tree)
        .forEach((fiasLevel: IObject) =>
          fiasLevel.houses
            ? fiasLevel.houses.forEach((houseObj: IObject) => {
                if (houseObj._id === houseId) {
                  house = houseObj;
                }
              })
            : this.getHouseById(houseId, fiasLevel.inheritors)
        )
        .flat(Infinity);

      return house;
    },
  }))
  .views((self) => ({
    get addressesList(): ISelectValue[] {
      return union(self.groupList, self.housesList);
    },
    get addressesListWithSectors(): ISelectValue[] {
      return union(self.groupListWithSectors, self.housesListWithSectors);
    },
  }))
  .actions((self) => ({
    setSelectedSectors(sectors: string[]) {
      self.selectedSectors = cast(sectors);
    },
    clearFiasSearch() {
      self.currentValueSearch = '';
      self.currentValueFlatSearch = '';
      self.showSecondOptionsField = false;
      self.flatLivingCount = 0;
      self.flatNotLivingCount = 0;
      self.flatParkingCount = 0;
      self.searchTree = [];
      self.searchTreeWithSectors = [];
      self.currentSelectedHouses = [];
      self.currentSelectedFlats = [];
      self.lastSelectedHouse = [];
      self.confirmedSelectedHouses = [];
      self.confirmedSelectedFlats = [];
      self.selectedGroups = [];
      self.selectedFias = [];
      self.selectedHouses = [];
    },
    clearSearchTree() {
      self.searchTree = [];
      self.searchTreeWithSectors = [];
    },
    setCurrentFiasSearchType(option: string) {
      self.currentFiasSearchType = option;
    },
    setCurrentValueSearch(address: string) {
      self.currentValueSearch = address;
    },
    setCurrentValueFlatSearch(flat: string) {
      self.currentValueFlatSearch = flat;
    },
    setCurrentSelectedHouses(houses: any) {
      self.currentSelectedHouses = houses;
    },
    setCurrentSelectedFlats(flats: any) {
      self.currentSelectedFlats = flats;
    },
    setLastSelectedHouse(house: any) {
      self.lastSelectedHouse = house;
    },
    setConfirmedSelectedHouses(houses: any) {
      self.confirmedSelectedHouses = houses;
    },
    setConfirmedSelectedFlats(flats: any) {
      self.confirmedSelectedFlats = flats;
    },
    setSelectedGroups(groups: any) {
      self.selectedGroups = groups;
    },
    setSelectedFias(addresses: any) {
      self.selectedFias = addresses;
    },
    setSelectedHouses(houses: any) {
      self.selectedHouses = houses;
    },
    setShowSecondOptionsField(bool: boolean) {
      self.showSecondOptionsField = bool;
    },
    clearAreas() {
      self.areasResultsGeneration += 1;
      self.areasLoadingGeneration += 1;
      applySnapshot(self.statuses.areasByHouse, {});
      self.areas = [];
    },
    setTree(tree: { [propName: string]: any }[]) {
      self.tree = tree;
    },
    clearTree() {
      self.tree = [];
    },
    getTree: flow(function* () {
      try {
        self.statuses.fiasTree.loadingStates();

        const response = yield Fias.GetTree();

        self.tree = response.tree;
        self.statuses.fiasTree.successStates();

        return self.tree;
      } catch (e) {
        console.error(e);
        self.statuses.fiasTree.errorStates();
      } finally {
        self.statuses.fiasTree.finallyStates();
      }
    }),
    getTreeWithSectors: flow(function* () {
      try {
        self.statuses.fiasTreeWithSectors.pending = true;
        self.statuses.fiasTreeWithSectors.fulfilled = false;
        self.statuses.fiasTreeWithSectors.error = false;

        const response = yield Fias.GetTreeWithSectors();

        self.treeWithSectors = response.tree;
        self.allAvailableSectors = cast(response.sectors);

        self.statuses.fiasTreeWithSectors.fulfilled = true;
      } catch (e) {
        console.error(e);
        self.statuses.fiasTreeWithSectors.error = true;
      } finally {
        self.statuses.fiasTreeWithSectors.pending = false;
      }
    }),
    getHouseGroups: flow(function* () {
      try {
        self.statuses.houseGroups.pending = true;
        const response = yield Fias.GetHouseGroups();
        self.houseGroups = response.results;
        self.statuses.houseGroups.fulfilled = true;
      } catch (e) {
        console.error(e);
        self.statuses.houseGroups.error = true;
      } finally {
        self.statuses.houseGroups.pending = false;
      }
    }),
    getHouseGroupsWithSectors: flow(function* () {
      try {
        self.statuses.houseGroupsWithSectors.pending = true;
        self.statuses.houseGroupsWithSectors.fulfilled = false;
        self.statuses.houseGroupsWithSectors.error = false;

        const response = yield Fias.GetHouseGroupsWithSectors();

        self.houseGroupsWithSectors = response.house_groups;
        self.statuses.houseGroupsWithSectors.fulfilled = true;
      } catch (e) {
        console.error(e);
        self.statuses.houseGroupsWithSectors.error = true;
      } finally {
        self.statuses.houseGroupsWithSectors.pending = false;
      }
    }),
    getAreasByHouse: flow(function* (id: string, shouldApply?: () => boolean) {
      self.areasResultsGeneration += 1;
      self.areasLoadingGeneration += 1;
      const resultsGeneration = self.areasResultsGeneration;
      const loadingGeneration = self.areasLoadingGeneration;
      const canApply = () =>
        resultsGeneration === self.areasResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.areasLoadingGeneration;

      applySnapshot(self.statuses.areasByHouse, {});

      try {
        self.statuses.areasByHouse.loadingStates();

        const response = yield Areas.GetAreasByHouseId(id);

        if (canApply()) {
          self.areas = response.results;
          self.statuses.areasByHouse.successStates();
        }

        return self.areas;
      } catch (e) {
        if (canApply()) {
          self.statuses.areasByHouse.errorStates();
        }
      } finally {
        if (canFinalize()) {
          self.statuses.areasByHouse.finallyStates();
        }
      }
    }),
    getFlatCount: flow(function* (id) {
      try {
        self.statuses.flatCount.fulfilled = false;
        self.statuses.flatCount.error = false;
        self.statuses.flatCount.pending = true;

        const response = yield Fias.GetFlatCount(id);

        self.flatLivingCount = response.count_data.LivingArea || 0;
        self.flatNotLivingCount = response.count_data.NotLivingArea || 0;
        self.flatParkingCount = response.count_data.ParkingArea || 0;
        self.statuses.flatCount.fulfilled = true;
      } catch (e) {
        self.statuses.flatCount.error = true;
        console.error(e);
      } finally {
        self.statuses.flatCount.pending = false;
      }
    }),
    getSearchTree: flow(function* (address) {
      try {
        self.statuses.searchTree.fulfilled = false;
        self.statuses.searchTree.error = false;
        self.statuses.searchTree.pending = true;

        const response = yield Fias.GetSearchTree(address);

        self.searchTree = response.tree;
        self.statuses.searchTree.fulfilled = true;
      } catch (e) {
        self.statuses.searchTree.error = true;
        console.error(e);
      } finally {
        self.statuses.searchTree.pending = false;
      }
    }),
    getSearchTreeWithSectors: flow(function* (address) {
      try {
        self.statuses.searchTreeWithSectors.pending = true;
        self.statuses.searchTreeWithSectors.fulfilled = false;
        self.statuses.searchTreeWithSectors.error = false;

        const response = yield Fias.GetSearchTreeWithSectors(address);

        self.searchTreeWithSectors = response.tree;
        self.statuses.searchTreeWithSectors.fulfilled = true;
      } catch (e) {
        self.statuses.searchTreeWithSectors.error = true;
        console.error(e);
      } finally {
        self.statuses.searchTreeWithSectors.pending = false;
      }
    }),
  }));
